import { createLog } from "@src/api/logs";
import { AccountBeforeConfirmTemplate } from "@src/components/templates/AccountBeforeConfirmTemplate";
import { pageConst } from "@src/constants/pageConst";
import { useSession } from "@src/hooks/useSession";
import { resolveStatusFromError } from "@src/utils/httpError";
import {
  ERROR_CODES,
  getErrorCodeFromStatus,
  redirectToErrorPage,
} from "@src/utils/sessionUtils";
import { checkSystemAvailability } from "@src/utils/systemAvailabilityCheck";
import { useRouter } from "next/router";
import { ComponentProps, useEffect, useState } from "react";

/**
 * AccountBeforeConfirmTemplate用injector
 * @constructor
 */

export function AccountBeforeConfirmInjector(): ComponentProps<
  typeof AccountBeforeConfirmTemplate
> {
  const { createSession } = useSession(false);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logMessage, setLogMessage] = useState<string>("");

  // 初回描画時にシステム稼働時間をチェックする
  useEffect(() => {
    (async () => {
      // システム稼働時間チェック
      const isAvailable = await checkSystemAvailability(router);
      if (!isAvailable) {
        setLogMessage("システム稼働時間外に表示されました。");
      }
    })();
  }, []);

  // ボタン押下時の処理
  const handleCreateSessionAndNext = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // セッションを作成
      const result = await createSession("メールアドレス登録フロー");
      // console.log("セッション作成成功:", result);

      if (result.status !== 200) {
        setIsLoading(false);
        const errorCode = getErrorCodeFromStatus(result.status);
        // セッション作成失敗
        redirectToErrorPage(
          router,
          errorCode,
          "セッション作成に失敗しました。"
        );
        // ログ作成にもセッションIDが必要なのでここで止める
        return;
      }

      const sessionId = result.data.session_id;
      // ログを作成
      try {
        const logInfo = {
          screen_name: "お申込み前のご確認画面",
          session_id: sessionId,
          operation_type: "view" as const,
          executed_at: new Date().toISOString(),
          user_agent: navigator.userAgent,
          comment: logMessage,
        };

        const logGroup = process.env.NEXT_PUBLIC_LOG_GROUP;

        const result = await createLog(logGroup, logInfo);
      } catch (error) {
        console.error("ログ作成失敗:", error);
      }

      // 成功したら次のページに遷移
      router.push(pageConst.accountTermsConfirm.href);
    } catch (error) {
      console.error("セッション作成失敗:", error);
      const status = resolveStatusFromError(error);
      const errorCode = status
        ? getErrorCodeFromStatus(status)
        : ERROR_CODES.SERVER_ERROR;

      // 例外時も可能な限り実際のステータス・エラーコードを渡す
      redirectToErrorPage(
        router,
        errorCode,
        "セッション作成に失敗しました",
        status
      );
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    animation: {},
    next: {
      onClick: handleCreateSessionAndNext,
    },
    isLoading,
    error,
  };
}
